// Angular and RJX Imports
// =========================================================
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  type OnInit,
  type SimpleChanges,
  type OnDestroy
} from '@angular/core'
import * as XLSX from 'xlsx'
import { v4 as uuidv4 } from 'uuid'
// Ag Grid Imports
// =========================================================
import {
  type GridReadyEvent,
  type GridApi,
  type CellEditingStoppedEvent,
  type RowDataUpdatedEvent,
  type ModelUpdatedEvent,
  ColDef
} from 'ag-grid-community'
// Custom Imports
// =========================================================
import { CustomGridOptions } from 'src/app/05_ag-grid-configs/02_global-settings/grid-options'
import { ProductUpload_GenerateGridData } from 'src/app/05_ag-grid-configs/04_generate-colDefs/product-upload-colDefs'
import { AttributeUpload_GenerateGridData } from 'src/app/05_ag-grid-configs/04_generate-colDefs/attribute-upload-colDefs'
import { FormatKey, IsKeyInObj, ReverseFormatKey } from 'src/app/utils/global_functions'
import { GridFileUploadService } from 'src/app/core/services/grid-file-upload-service.service'
import { JobUpload_GenerateGridData } from 'src/app/05_ag-grid-configs/04_generate-colDefs/job-upload-colDefs'
import moment from 'moment'
import { RecoBuyQtyUpload_GenerateGridData } from 'src/app/05_ag-grid-configs/04_generate-colDefs/reco-buy-qty-upload-colDefs'

@Component({
  standalone: false,
  selector: 'app-upload-grids',
  templateUrl: './upload-grids.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./upload-grids.component.scss']
})
export class UploadGridsComponent implements OnInit, OnDestroy {
  // Ag Grid Configurations
  public popupParent: HTMLElement | null = document.querySelector('body')
  private readonly customGridOptions: any = {
    ...CustomGridOptions,
    context: {
      componentParent: this,
      onDeleteRow: params => this.onDeleteRow(params),
      pageTitle: ''
    }
  }

  @Input() colDefJSONSettings: any
  @Input() generateGridColumnsFunc: string
  @Input() gridTitle: string
  @Input() displayHeader: boolean
  @Input() exportFileName: string
  @Input() addRowEnabled: boolean
  @Input() autoSizeColumns: boolean

  autoSelectOptions: any // the key must match a grid column key and the value must be a string array
  // Set the auto select options
  @Input('autoSelectOptions')
  set _autoSelectOptions (data: any) {
    console.log('---> autoSelectOptions updated: ', data)
    this.autoSelectOptions = data
  }

  noDuplicates: any // the key must match a grid column key and the value must be a string array
  @Input('noDuplicates')
  set _noDuplicates (data: any) {
    console.log('---> noDuplicates allowed updated: ', data)
    this.noDuplicates = data
  }

  checkDuplicatesAPIFunc: any
  @Input('checkDuplicatesAPIFunc')
  set _checkDuplicatesAPIFunc (data: any) {
    console.log('---> checkDuplicatesAPIFunc allowed updated: ', data)
    this.checkDuplicatesAPIFunc = data
  }

  checkPermissionsAPIFunc: any
  @Input('checkPermissionsAPIFunc')
  set _checkPermissionsAPIFunc (data: any) {
    console.log('---> checkPermissionsAPIFunc updated: ', data)
    this.checkPermissionsAPIFunc = data
  }

  uploadIsEnabled: boolean
  @Input('uploadIsEnabled')
  set _uploadIsEnabled (toggleUpload: boolean) {
    // console.log('---> upload button state toggleUpload: ', toggleUpload)
    this.uploadIsEnabled = toggleUpload
  }

  processingData: boolean
  @Input('processingData')
  set _processingData (processing: boolean) {
    console.log('---> Processing Data: ', processing)
    this.processingData = processing
  }

  selectionValues: any

  @Input('selectionValues')
  set _selectionValues (data: any) {
    console.log('---> SelectionValues Data: ', data)
    this.selectionValues = data
  }

  @Output() onGridDataUpdated: EventEmitter<any> = new EventEmitter<any>()
  // Data set by the incoming column definitions
  requiredFields: string[]
  requiredFieldsFormatted: string[]
  optionalInputs: string[] = []

  @Input() prodFields: string[] // product id fields that either must exist or must not exist, depending on what we are uploading
  @Input() prodsMustExist: boolean = true // whether the products in the prodFields must already exist or must not already exist
  @Input() requiredPairs = [] // if one exists, the other must too
  @Input() requiredPairsDirectional = [] // if the first one exists, the other must too
  @Input() requiredORPairs = [] // either one or both must exist
  @Input() requiredXORPairs = [] // one and only one must exist
  @Input() positiveNumberFields = [] // these fields are numeric, and must be positive numbers
  @Input() nonNegativeNumberFields = [] // these fields are numeric, and must be nonnegative numbers (they can be positive or 0)
  @Input() noDuplicatesInSubmission = [] // no two rows in the uploaded file may share a value for these fields

  blankRowData: any = {}
  @Input()
    templateDownloadMessage: string =
      '** Please complete all of the mandatory fields below for all rows before uploading. **'

  // Ag Grid Configuration
  public gridApi: GridApi
  columnApi: {}
  gridOptions: any = {}
  isAgGridLoading: boolean = true
  // Grid Data
  columnDefs: any[] = []
  rowData = []

  // File upload settings
  fileData: any = []
  headers: any[] = []
  wopts: XLSX.WritingOptions = { bookType: 'xlsx', type: 'array' }
  fileName: string = 'SheetJS.xlsx'
  dlmOpts: string[] = ['DLM = 1', 'DLM = 2', 'DLM = 3', 'DLM = 4']

  tableResetDisabled: boolean = true

  @ViewChild('fileUpload')
    fileUpload

  constructor (private readonly gridFileUploadService: GridFileUploadService) {
    this.gridOptions = {
      ...this.customGridOptions,
      rowClassRules: {
        'parent-row-warning': params => {
          const status = (params.data?.status && params.data.status.toLowerCase()) || null
          return status === 'failed' || (status && status.includes('error'))
        }
      },
      onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
      getRowId: node => node?.data.id,
      onCellEditingStopped: async (event: CellEditingStoppedEvent) => this.checkRowData(event),
      onModelUpdated: (event: ModelUpdatedEvent) => {
        const rowsAvailable = event.api.getDisplayedRowCount() === 0
        const el = <HTMLInputElement>document.getElementById(this.gridTitle + '-reset')
        el.disabled = rowsAvailable
        this.tableResetDisabled = rowsAvailable
      },
      onRowDataUpdated: async (event: RowDataUpdatedEvent) => {
        if (this.autoSizeColumns) event.api.autoSizeAllColumns()
        event.api.autoSizeColumns(['status'])
        await this.checkRowData(event)
      },
      overlayNoRowsTemplate: `
      <div class="ag-overlay-loading-center">
        <div class="far fa-frown">
        Please do one of the following:
          <ol style="text-align: start;
          padding-right: 20px;"
      }>
              <li> Upload a .csv or .xlsx file </li>
              <li> Download the correctly formatted template </li>
          </ol>
        </div>
      </div>`
    }
  }

  ngOnInit () {
    console.log('this.gridTitle', this.gridTitle)
    // Update the grid name
    this.gridOptions.context.pageTitle = this.exportFileName

    this.requiredFields = Object.keys(this.colDefJSONSettings[0]).filter((field, i) => {
      const f = FormatKey(field)
      if (this.colDefJSONSettings[0][field]?.refData?.optionalInput) {
        this.optionalInputs.push(f)
      }
      return f !== 'delete' && f !== 'status'
    })
    // Define Blank Row Data
    this.requiredFieldsFormatted = this.requiredFields.map(field => {
      this.blankRowData[field] = ''
      return FormatKey(field)
    })
    // console.log('required fields: ', this.requiredFields);
    // console.log('requiredFieldsFormatted: ', this.requiredFieldsFormatted);
    // console.log('blank row Data: ', this.blankRowData);
  }

  ngOnChanges (changes: SimpleChanges): void {
    // console.log('CHANGES: ', changes)
  }

  // Render AG Grid Column Definitions
  async renderGrid (mainColDefs) {
    const params = {
      mainColDefs
    }
    const getGridData = async () => {
      switch (this.generateGridColumnsFunc) {
        case 'ProductUpload_GenerateGridData':
          return await ProductUpload_GenerateGridData(params)
        case 'AttributeUpload_GenerateGridData':
          return await AttributeUpload_GenerateGridData(params)
        case 'JobUpload_GenerateGridData':
          return await JobUpload_GenerateGridData(params)
        case 'RecoBuyQtyUpload_GenerateGridData':
          return await RecoBuyQtyUpload_GenerateGridData(params)
      }
    }
    const gridData = await getGridData()

    if (gridData) {
      const checkAgGrid = () => {
        if (!this.isAgGridLoading) {
          if (this.gridApi && !this.gridApi['destroyCalled']) {
            console.log(`---> Upload Grid Column: ${this.generateGridColumnsFunc}`, gridData.mainColDefs)

            this.columnDefs = gridData.mainColDefs
            this.gridApi?.setGridOption('columnDefs', gridData.mainColDefs)
            this.gridApi?.setGridOption('rowData', [])
            // this.columnApi.autoSizeColumns(['status'])
          }

          clearInterval(setData)
        } else {
          console.log('Ag grid is loading')
        }
      }
      const setData = setInterval(checkAgGrid, 100)
    }
  }

  onGridReady (event: GridReadyEvent) {
    this.gridApi = event.api
    this.columnApi = event.api
    this.isAgGridLoading = false

    this.gridApi.showLoadingOverlay()

    this.renderGrid(this.colDefJSONSettings[0])
  }

  @HostListener('keydown', ['$event'])
  onKeydown (event) {
    event.stopPropagation()
    if (event.key == 'Escape') {
      return false
    }

    if (event.key == 'Enter' || event.key == 'Tab') {
      return false
    }

    if (event.key == 'ArrowUp' || event.key == 'ArrowDown') {
      return false
    }
  }

  onUploadFile (event) {
    if (this.fileUpload) {
      this.fileUpload.basicFileInput.nativeElement.click()
    }
  }

  onFileChange (event: any, fileUpload) {
    // format fields for error messages

    /* wire up file reader */
    const target: DataTransfer = <DataTransfer>event
    Object.keys(event.currentFiles).forEach(key => {
      const reader: FileReader = new FileReader()

      reader.onload = (e: any) => {
        /* read workbook */
        const bstr: string = e.currentTarget.result
        const wb: XLSX.WorkBook = XLSX.read(bstr, {
          type: 'binary',
          FS: ','
        })

        /* grab first sheet */
        const wsname: string = wb.SheetNames[0]
        const ws: XLSX.WorkSheet = wb.Sheets[wsname]

        /* save data */
        const fileData = <any>XLSX.utils.sheet_to_json(ws, { header: 1, raw: false })
        if (fileData[0].length === 1) {
          fileData.splice(0, 1)
        }

        /* Transform data  */
        if (fileData) {
          this.fileData = fileData
          console.log('Form Data: ', this.fileData)
          // check to make sure that the headers from the file match the required fields for the grid
          const formattedHeaders = this.fileData[0].map(header => FormatKey(header?.trim()))
          const correctHeaders = formattedHeaders.filter(header => {
            return this.requiredFieldsFormatted.includes(header)
          })

          if (this.requiredFields.length === correctHeaders.length) {
            // Format data for AG Grid table
            const mappedData = Object.entries(this.fileData).map(([key, value], rowI) => {
              // The headers will always be the first item in the array
              // if ((key === '0') || (key === '1' && this.fileData[0] === this.downloadMessage) ) {
              if (key === '0') {
                return { headers: correctHeaders }
              } else {
                const tempRow = {
                  status: null,
                  error_reason: null,
                  id: uuidv4()
                }
                // Loop through the required fields to make sure that the correct information is present
                this.requiredFields.map((field, i) => {
                  const formattedField = FormatKey(field)
                  const fieldIndex = formattedHeaders.indexOf(formattedField)
                  console.log('formattedField: ', formattedField)
                  console.log('fieldIndex: ', fieldIndex)
                  let cellValue = value[fieldIndex]
                    ? value[fieldIndex].toString().replaceAll(/"/g, '').trim()
                    : value[fieldIndex]
                  console.log('cellValue: ', cellValue, i)
                  tempRow[formattedField] = cellValue

                  if (formattedField === 'prior_selling_season' && cellValue !== undefined) {
                    this.optionalInputs = this.optionalInputs.filter(
                      v => v !== 'prior_selling_season'
                    )
                  } else if (formattedField === 'prior_selling_season' && cellValue === undefined) {
                    this.optionalInputs = this.optionalInputs.includes('prior_selling_season')
                      ? this.optionalInputs
                      : [...this.optionalInputs, 'prior_selling_season']
                  }

                  // Function to generate error messages if data is missing or incorrect data is trying to be uploaded
                  const setRowDataUploadError = (isMissing: boolean, customValue = null) => {
                    // tempRow[formattedField] = null
                    tempRow.status = 'Error'
                    // Set Error Message
                    if (tempRow.error_reason) {
                      tempRow.error_reason =
                                                tempRow.error_reason +
                                                `, ${field.toUpperCase()} = ${customValue || cellValue}`
                    } else if (isMissing) {
                      tempRow.error_reason = `The following fields are required: ${field.toUpperCase()}`
                    } else {
                      tempRow.error_reason = `Invalid data for the following field(s): ${field.toUpperCase()} = ${
                                                customValue || cellValue
                                            }`
                    }
                  }
                  if (cellValue === undefined || cellValue === '' || cellValue === null) {
                    if (!this.optionalInputs.includes(formattedField)) {
                      setRowDataUploadError(true)
                    } else {
                      tempRow[formattedField] = null
                    }
                  } else {
                    // All required data cells except for sizeRange have a datatype of selectionAutoComplete
                    // meaning that their values must be included in their respective key from the autoSelectOptions vairiable
                    // the autoSelectOptions is populated with data from the backend
                    if (
                      formattedField === 'eligible_sizes_(_|_)' ||
                                            formattedField === 'size_range_(_|_)'
                    ) {
                      // needs to be an array
                      // if it's valid input, we'll overwrite it later with an actual size array
                      cellValue = cellValue.toString()
                      tempRow[formattedField] = [cellValue]
                      const sizesArray =
                                                cellValue.length === 1
                                                  ? [cellValue]
                                                  : cellValue.includes('|')
                                                    ? cellValue.split('|')
                                                    : cellValue.split(',')
                      console.log('this.autoSelectOptions', this.autoSelectOptions)
                      console.log('sizesArray', sizesArray)
                      const incorrectSizes = sizesArray.filter(
                        size => !this.autoSelectOptions?.size_list.includes(size)
                      )
                      console.log('---- Size Validator ----: ', incorrectSizes)
                      if (incorrectSizes.length === 0) {
                        tempRow[formattedField] = this.autoSelectOptions?.size_list.filter(
                          size => sizesArray.includes(size)
                        )
                      } else {
                        setRowDataUploadError(
                          !this.optionalInputs.includes(formattedField) && !cellValue,
                          sizesArray.join(', ')
                        )
                      }
                    } else if (formattedField === 'buying_season') {
                      console.log(
                        'formattedField is buying_seasons and has seletionvalues',
                        this.selectionValues.buying_seasons
                      )
                      console.log('and cellValue', cellValue)
                      if (
                        !this.selectionValues.buying_seasons.some(
                          season => season.season === cellValue
                        )
                      ) {
                        setRowDataUploadError(
                          !this.optionalInputs.includes(formattedField) && !cellValue
                        )
                      }
                    } else if (formattedField === 'channel') {
                      if (!this.selectionValues.channels.some(channel => channel === cellValue)) {
                        setRowDataUploadError(
                          !this.optionalInputs.includes(formattedField) && !cellValue
                        )
                      }
                    } else if (formattedField === 'prior_selling_season') {
                      if (
                        !this.selectionValues.prior_selling_seasons.some(
                          season => season.season === cellValue
                        )
                      ) {
                        setRowDataUploadError(
                          !this.optionalInputs.includes(formattedField) && !cellValue
                        )
                      }
                    } else if (IsKeyInObj(this.autoSelectOptions, formattedField)) {
                      const availOptions = this.autoSelectOptions[formattedField] || null
                      if (availOptions) {
                        const foundUpperCase = availOptions.includes(
                          isNaN(Number(cellValue)) ? cellValue.toUpperCase() : cellValue
                        )
                        const foundLowerCase = availOptions.includes(cellValue)
                        if (foundUpperCase || foundLowerCase) {
                          tempRow[formattedField] = foundUpperCase
                            ? cellValue.toUpperCase()
                            : cellValue
                        } else {
                          setRowDataUploadError(
                            !this.optionalInputs.includes(formattedField) && !cellValue
                          )
                        }
                      } else {
                        setRowDataUploadError(
                          !this.optionalInputs.includes(formattedField) && !cellValue
                        )
                      }
                    } else {
                      tempRow[formattedField] = cellValue
                    }
                  }
                })
                console.log('tempRow', tempRow)
                return tempRow
              }
            })
            console.log('mappedData[1]', mappedData[1])
            // filter out extra headers if they are present
            const tableData = mappedData.filter((item, i) => i !== 0)
            // append the imported data to the grid
            this.gridApi.applyTransaction({
              add: tableData,
              addIndex: 0
            })

            // Clear Upload form
            fileUpload.clear()
          } else {
            console.error('Unable to process file upload')
            this.onAddRow({
              status: 'Upload Error',
              error_reason:
                                'Unable to process file. Files can only be read if the delimiter is a comma. Please make sure the column names are presented in the following manner: Season, Channel, Product ID, Size Offering.'
            })
            // Clear Upload form
            fileUpload.clear()
          }
        }
      }
      reader.readAsBinaryString(target.files[0])
    })
  }

  onAddRow (error?) {
    const setError = error || {}

    this.gridApi.applyTransaction({
      add: [{ ...this.blankRowData, id: uuidv4(), ...setError }],
      addIndex: 0
    })
  }

  onResetTable () {
    this.gridApi.setGridOption('rowData', [])
  }

  onDeleteRow (params) {
    console.log('delete a row: ', params)
    this.gridApi.applyTransaction({
      remove: [{ id: params.node.data.id }]
    })
  }

  onDownloadTemplate () {
    const exportKeys = this.requiredFields.map(field => {
      const def = this.colDefJSONSettings[0][field]
      if (def['refData'] && def['refData']['custom_field']) {
        return def['refData']['custom_field']
      } else {
        return FormatKey(field)
      }
    })
    this.gridApi.exportDataAsExcel({
      columnKeys: exportKeys,
      allColumns: false,
      fileName: `${this.exportFileName}_` + moment().format('LLL'),
      // skipHeader: false,
      onlySelected: true,
      prependContent: [
        {
          cells: [
            {
              data: {
                value: this.templateDownloadMessage,
                type: 'String'
              },
              mergeAcross: this.requiredFieldsFormatted.length - 1,
              styleId: 'upload-grid-header'
            }
          ]
        }
      ],
      pageSetup: {
        orientation: 'Landscape',
        pageSize: 'Letter'
      },
      margins: {
        top: 1,
        right: 1,
        bottom: 1,
        left: 1,
        header: 0.5,
        footer: 0.5
      }
    })
  }

  checkRowData (event: RowDataUpdatedEvent | CellEditingStoppedEvent) {
    console.log('---> Validating Row Data after event', event)
    const updatedRowData: {
      string?: any
    } = {}
    this.rowData = []
    this.gridApi.forEachLeafNode(node => {
      const data = node.data
      console.log('adding node.data', node.data)
      if (data) {
        // Track rowData to be updated
        updatedRowData[data.id] = data
        // Track current rowData
        this.rowData.push({ ...data })
      }
    })

    let allProductNodes = []
    Object.values(updatedRowData).forEach(row => {
      allProductNodes = allProductNodes.concat(this.prodFields.map(field => row[field]))
    })

    // Check duplicates across rows (independent of API calls)
    const duplicateValuesMap = {}
    this.noDuplicatesInSubmission.forEach(field => {
      const allValues = Object.entries(updatedRowData).map(([key, value]) => {
        return value[field]
      })
      const seen = new Set()
      const duplicateValuesSet = new Set()

      allValues.forEach(item => {
        if (seen.has(item)) {
          duplicateValuesSet.add(item)
        } else {
          seen.add(item)
        }
      })

      duplicateValuesMap[field] = Object.fromEntries(
        Object.entries(updatedRowData).filter(([key, value]) => {
          return Array.from(duplicateValuesSet).includes(value[field])
        })
      )
    })

    const permissionsPromise = this.checkPermissionsAPIFunc
      ? this.checkPermissionsAPIFunc([allProductNodes])
      : Promise.resolve(null)

    Promise.all([this.checkDuplicatesAPIFunc([allProductNodes]), permissionsPromise]).then(
      ([duplicatesData, permissionsData]) => {
        console.log('duplicatesData.data', duplicatesData.data)
        const badProducts = []
        const badProductRows = Object.fromEntries(
          Object.entries(updatedRowData).filter(([key, value]) => {
            let result = false
            this.prodFields.forEach(field => {
              const r = this.prodsMustExist == !duplicatesData.data.includes(value[field])
              if (r) {
                badProducts.push(value[field]) // collect bad products
              }
              result = result || r
            })
            return result // collect rows with bad products
          })
        )

        const notPermittedProducts = []
        const notPermittedProductRows = permissionsData
          ? Object.fromEntries(
            Object.entries(updatedRowData).filter(([key, value]) => {
              let result = false
              this.prodFields.forEach(field => {
                // Only flag permission error if the product exists (existence check takes priority)
                const productExists = duplicatesData.data.includes(value[field])
                const r = productExists && !permissionsData.data.includes(value[field])
                if (r) {
                  notPermittedProducts.push(value[field])
                }
                result = result || r
              })
              return result
            })
          )
          : {}

        this.checkRowDataCallback(
          updatedRowData,
          badProductRows,
          badProducts,
          duplicateValuesMap,
          notPermittedProductRows,
          notPermittedProducts
        )
      }
    )
  }

  checkRowDataCallback (
    updatedRowData: {
      string?: any
    },
    badProductRows = {},
    badProducts = [],
    duplicateValuesMap = {},
    notPermittedProductRows = {},
    notPermittedProducts = []
  ) {
    console.log('badProducts', badProducts)
    let rowDataIncomplete = false

    try {
      // After the most current row data is collected => validate the data
      if (Object.keys(updatedRowData).length > 0) {
        const updatedRows = Object.entries(updatedRowData).map(([key, value]) => {
          const dup = 'duplicate_data'
          const idString = 'id'
          const id = value[idString]
          const data = value
          const incompleteFields = []

          // prod existance validation
          this.prodFields.forEach(fieldName => {
            console.log('validating field name', fieldName)
            if (badProductRows[id] && badProducts.includes(data[fieldName])) {
              rowDataIncomplete = true
              Object.assign(updatedRowData[id], {
                error_reason:
                                    `${ReverseFormatKey(fieldName).toUpperCase()}: ${data[fieldName]} ` +
                                    (this.prodsMustExist ? ' does not exist.' : ' already exists.'),
                status: 'Error'
              })
            }
          })

          // prod permissions validation
          this.prodFields.forEach(fieldName => {
            if (notPermittedProductRows[id] && notPermittedProducts.includes(data[fieldName])) {
              rowDataIncomplete = true
              Object.assign(updatedRowData[id], {
                error_reason: `User does not have permission for ${ReverseFormatKey(
                                    fieldName
                                ).toUpperCase()}: ${data[fieldName]}`,
                status: 'Error'
              })
            }
          })

          // pair validation
          this.requiredPairs.forEach(pair => {
            if (updatedRowData[id][pair[0]] && !updatedRowData[id][pair[1]]) {
              rowDataIncomplete = true
              Object.assign(updatedRowData[id], {
                error_reason: `If ${ReverseFormatKey(
                                    pair[0]
                                ).toUpperCase()} is provided, ${ReverseFormatKey(
                                    pair[1]
                                ).toUpperCase()} must be provided.`,
                status: 'Error'
              })
            }
            if (updatedRowData[id][pair[1]] && !updatedRowData[id][pair[0]]) {
              rowDataIncomplete = true
              Object.assign(updatedRowData[id], {
                error_reason: `If ${ReverseFormatKey(
                                    pair[1]
                                ).toUpperCase()} is provided, ${ReverseFormatKey(
                                    pair[0]
                                ).toUpperCase()} must be provided.`,
                status: 'Error'
              })
            }
          })
          this.requiredPairsDirectional.forEach(pair => {
            if (updatedRowData[id][pair[0]] && !updatedRowData[id][pair[1]]) {
              rowDataIncomplete = true
              Object.assign(updatedRowData[id], {
                error_reason: `If ${ReverseFormatKey(
                                    pair[0]
                                ).toUpperCase()} is provided, ${ReverseFormatKey(
                                    pair[1]
                                ).toUpperCase()} must be provided.`,
                status: 'Error'
              })
            }
          })
          this.requiredORPairs.forEach(pair => {
            if (!updatedRowData[id][pair[0]] && !updatedRowData[id][pair[1]]) {
              rowDataIncomplete = true
              Object.assign(updatedRowData[id], {
                error_reason: `At least one of ${ReverseFormatKey(
                                    pair[0]
                                ).toUpperCase()} or ${ReverseFormatKey(pair[1]).toUpperCase()} must be provided.`,
                status: 'Error'
              })
            }
          })
          this.requiredXORPairs.forEach(pair => {
            if (
              (updatedRowData[id][pair[0]] && updatedRowData[id][pair[1]]) ||
                            (!updatedRowData[id][pair[0]] && !updatedRowData[id][pair[1]])
            ) {
              rowDataIncomplete = true
              Object.assign(updatedRowData[id], {
                error_reason: pair[1].startsWith('custom_season') // season start and end go together, so the error message should clarify they are both needed
                  ? `Either ${ReverseFormatKey(
                                          pair[0]
                                      ).toUpperCase()} or both CUSTOM SEASON START + CUSTOM SEASON END must be provided.`
                  : `Either ${ReverseFormatKey(pair[1]).toUpperCase()} or ${ReverseFormatKey(
                                          pair[0]
                                      ).toUpperCase()} must be provided.`,
                status: 'Error'
              })
            }
          })

          // Other per-field validation
          // use requiredFields so we can get the coldef
          this.requiredFields.forEach(async (field, i) => {
            const colDef = this.colDefJSONSettings[0][field]
            field = FormatKey(field) // go back to formatted fields
            // if data is missing from any of the required fields -> rowdata is incomplete
            if ((!data[field] || data[field] === '') && !this.optionalInputs.includes(field)) {
              if (field === 'prior_selling_season') {
                if (
                  data['custom_season_start'] === null ||
                                    data['custom_season_end'] === null ||
                                    data['custom_season_start'] === undefined ||
                                    data['custom_season_end'] === undefined
                ) {
                  rowDataIncomplete = true
                  incompleteFields.push(field)
                  Object.assign(updatedRowData[id], {
                    error_reason:
                                            'One of Prior Selling Season and Custom Start/End dates are required. Please delete this row to continue',
                    status: 'Error'
                  })
                }
              } else {
                rowDataIncomplete = true
                incompleteFields.push(field)
              }
            }
            if (IsKeyInObj(this.noDuplicates, field) && this.noDuplicates[field].includes(data[field])) {
              rowDataIncomplete = true
              Object.assign(updatedRowData[id], {
                error_reason: `${ReverseFormatKey(field).toUpperCase()}: ${
                                    data[field]
                                } already exists.`,
                status: 'Error'
              })
            }
            if (duplicateValuesMap[field] && duplicateValuesMap[field][id]) {
              rowDataIncomplete = true
              Object.assign(updatedRowData[id], {
                error_reason: `${ReverseFormatKey(field).toUpperCase()}: ${
                                    data[field]
                                } already exists in this table.`,
                status: 'Error'
              })
            }

            const positive = this.positiveNumberFields.includes(field)
            const nonNegative = this.nonNegativeNumberFields.includes(field)
            let parseFunction = parseFloat
            switch (colDef.datatype) {
              case 'integer':
                parseFunction = parseInt
                // eslint-disable-next-line no-fallthrough
              case 'number':
              case 'currency':
                // ensures field is a positive float/integer, if given
                // if it is a required but not provided, that will be caught elsewhere
                if (data[field] !== '' && data[field] !== null && typeof data[field] === 'string') {
                  let val = data[field].replaceAll(',', '') // the file may have commas in the field
                  val = val.startsWith('$') ? val.slice(1) : val // if it's a currency column we allow a leading $
                  val = parseFunction(val)
                  if (isNaN(val) || (positive && val <= 0) || (nonNegative && val < 0)) {
                    // construct type statement
                    const numberType =
                                            (positive
                                              ? ' positive'
                                              : nonNegative
                                                ? ' non-negative'
                                                : colDef.datatype === 'integer'
                                                  ? 'n'
                                                  : '') +
                                                colDef.datatype ===
                                            'integer'
                                              ? ' integer'
                                              : ' decimal number'
                    rowDataIncomplete = true
                    Object.assign(updatedRowData[id], {
                      error_reason: `${ReverseFormatKey(
                                                field
                                            ).toUpperCase()} must be a${numberType}.`, // no space is intentional for 'a'/'an' construction
                      status: 'Error'
                    })
                  }
                  // update the actual value to be numeric
                  updatedRowData[id][field] = parseFunction(val as unknown as string)
                }
                break
            }

            if (i + 1 === this.requiredFields.length) {
              // Prevent Duplicate data from being sent to the backend
              const matches = Object.values(updatedRowData).filter(key => {
                const hasMatch = [
                  ...new Set(
                    this.requiredFieldsFormatted.map(rff => {
                      return key[rff] === data[rff]
                    })
                  )
                ]
                return key[idString] !== data[idString] && !hasMatch.includes(false)
              })

              if (matches.length > 0) {
                rowDataIncomplete = true

                if (incompleteFields.length === 0) {
                  const ids = matches.map(row => row[idString])
                  const errorObject = {
                    duplicate_data: [data[idString], ...ids],
                    error_reason: 'Duplicated row data',
                    status: 'Error'
                  }
                  errorObject.duplicate_data.forEach(dup => {
                    Object.assign(updatedRowData[dup], errorObject)
                  })
                }
              } else if (matches.length === 0 && data[dup] && data[dup].length > 0) {
                // Remove the duplicate id from all rows
                data[dup].forEach(dups => {
                  // Check to make sure all duplicate rows still exists (some may have been deleted)
                  const index = IsKeyInObj(updatedRowData, dups)
                    ? updatedRowData[dups][dup].indexOf(id)
                    : -1

                  if (index > -1) {
                    Object.assign(updatedRowData[dups], {
                      duplicate_data: null,
                      error_reason: null,
                      status: null
                    })
                  }
                })
              }
              // check for updates to upload errors
              if (
                i + 1 === this.requiredFields.length &&
                                updatedRowData[id]?.status &&
                                updatedRowData[id]?.status === 'Upload Error' &&
                                incompleteFields.length === 0
              ) {
                if (!updatedRowData[id]['error_reason'].includes('Prior Selling Season')) {
                  Object.assign(updatedRowData[id], {
                    error_reason: null,
                    status: null
                  })
                }
              }
            }
          })
          return value
        })

        console.log('updatedRows', JSON.stringify(updatedRows))

        // Update Ag Grid with the new data
        if (updatedRows) {
          const updateRows = Object.values(updatedRowData)
          console.log('this.columnDefs', this.columnDefs)
          // map cols with custom fields
          this.columnDefs.forEach(colDef => {
            console.log('checking colDef', colDef, 'for custom field')
            if (colDef.refData?.custom_field) {
              console.log('found custom field', colDef.refData.custom_field)
              updateRows.forEach(row => {
                console.log('row is', JSON.stringify(row))
                console.log('row has value', row[colDef.refData.custom_field])
                console.log('formatted key is', FormatKey(colDef.headerName))
                console.log('overwriting with value', row[FormatKey(colDef.headerName)])
                // move values over to custom field
                row[colDef.refData.custom_field] = row[FormatKey(colDef.headerName)]
                // delete row[FormatKey(colDef.headerName)]
              })
            }
          })
          if (JSON.stringify(this.rowData) !== JSON.stringify(updateRows)) {
            this.rowData = updateRows
            this.gridApi.applyTransaction({ update: updateRows })
          }
        }
      }
    } catch (error) {
    } finally {
      const noRows = this.rowData.length === 0
      // Disable the submit functionality if the rowData is incomplete
      rowDataIncomplete = noRows ? true : rowDataIncomplete

      console.log('--> Uploaded Row Data: ', this.rowData)
      console.log('---> Row Data is Incomplete: ', rowDataIncomplete)

      this.gridFileUploadService.onUpdateRowDataErrors({
        rowDataIncomplete,
        rowData: this.rowData,
        gridTitle: this.gridTitle,
        agGrid: this.gridApi
      })
    }
  }

  ngOnDestroy () {
    if (this.gridApi) {
      this.gridApi.flushAsyncTransactions()
      this.gridApi.expireValueCache()
      this.gridApi = null
    }
  }
}
